import React, { useState, useCallback } from 'react';
import TileView from '@nykaa/combo-products/components/ProductTile';
import { useSelector } from 'react-redux';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import styled from 'App/styled';
import {
  selectBrandName,
  selectCurrentSku,
  selectProductMedia,
  selectProductRelatedVideo,
} from 'shared/store/product/selector';
import { State } from 'shared/store/types';
import { PageSource } from 'constants/dataLayer';
import Modal from 'shared/components/Modal';
import CrossIconBlack from 'components/Icons/CircleCloseBlack';
import AddToCartButton from '../../layouts/Product/Actions/AddToCart';
import SelectedMedia from './SelectedMedia';
import MediaSelector from './MediaSelector';
import MediaSection from './MediaSection';
import NotifyMe from 'shared/layouts/Product/Actions/NotifyMe';
import { isRegisteredViaMobile } from 'store/auth/helper';
import ActionWrapper from 'components/CardAction';
import { ProductRenderProps } from 'types/product';
import { CUSTOM_WISHLIST_CLASS_VIDEO_MODAL } from 'constants/product';
import WishlistIcon from '../../layouts/Product/Actions/Wishlist';
import { pushEvent } from 'helpers/utils/dataLayer';
import {
  PDP_IMAGE_POPUP_OPEN,
  PDP_VIDEO_POPUP_OPEN,
  VIDEO_TYPE,
} from 'components/CustomVideoPlayer/constants';
import useShouldDisableCta from 'hooks/Product/action';

interface MediaModalProps {
  setModalState: (args: boolean) => void;
  currentTabView: string;
  primaryCategories: any;
  currentVideo: any;
  currentImage: any;
}

const InnerWrap = styled.div<{ isVideo: boolean }>`
  width: 1044px;
  margin: 3% auto;
  bottom: 0;
  line-height: 18px;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borders.radius40};
  height: 90%;
  max-width: 1200px;
  @media only screen and (min-width: 768px) and (max-width: 1024px) {
    width: 750px;
    height: 96%;
    margin: 6% auto;
  }
  .close-button {
    right: 5px;
    top: 5px;
    background: none;
    border: none;
    svg {
      display: inline-block;
      width: 15px;
      height: 15px;
    }
  }
`;
const ContentWrapper = styled.div`
  display: flex;
  height: 100%;
`;

const ContentSection = styled.div<{ isVideo: boolean }>`
  display: flex;
  flex-direction: column;
  width: 330px;
  max-width: 330px;
  border-left: 1px solid
    ${({ theme }) => hexToRgb(theme.colors.textPrimary, 0.16)};
  border-radius: ${({ theme }) =>
    `0 ${theme.borders.radius40} ${theme.borders.radius40} 0`};
  background-color: ${({ theme }) => theme.colors.surface};
`;

let modalStyle = {
  width: '1044px',
  position: 'fixed',
  left: '0',
  margin: '0 auto',
  maxWidth: '1200px',
};

const ActionWrappers = styled.div<{ isVideo: boolean; isDisabled?: boolean }>`
  padding: 10px ${({ theme }) => theme.spacing.spacing120};
  svg {
    display: none;
  }
  button {
    border-radius: ${({ theme }) => theme.borders.radius20};
    height: 35px;
    width: 285px;
    ${({ theme }) => theme.typography.type162};
    color: ${({ theme }) => theme.colors.surface};
    background: ${({ theme: { colors }, isDisabled }) =>
      isDisabled ? colors.textDisabled : colors.primary};
  }
  border-top: 1px solid
    ${({ theme }) => hexToRgb(theme.colors.textPrimary, 0.16)};
  .video-tile {
    border: none;
    background-color: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.surfaceInverse40};
    img {
      display: none;
    }
    div:nth-of-type(2n) {
      div {
        color: ${({ theme }) => theme.colors.textPrimary};
        ${({ theme }) => theme.typography.bodyMedium};
      }
      span:nth-of-type(2n) {
        color: ${({ theme }) => theme.colors.textPrimary};
        ${({ theme }) => theme.typography.subTitleLarge};
      }
      span:nth-of-type(3n) {
        color: ${({ theme }) => theme.colors.primary};
        ${({ theme }) => theme.typography.subTitleLarge};
      }
    }
  }
  .custom-wishlist-button-video-modal {
    .wishlist_button {
      color: ${({ theme }) => theme.colors.surface};
      ${({ theme }) => theme.typography.titleXSmall};
    }
  }
  .video-tile > div:first-of-type {
    display: none;
  }
  .video-modal-section {
    span {
      color: ${({ theme }) => hexToRgb(theme.colors.textPrimary, 0.92)};
    }
    div:nth-of-type(1n) {
      flex-direction: column;
      margin-top: 10px;
      button {
        margin: 20px 0 10px 0;
        ${({ theme }) => theme.typography.titleXSmall};
        color: ${({ theme }) => theme.colors.surface};
      }
      input {
        margin-right: 0;
        max-width: 285px;
      }
    }
  }
`;

const CloseIconWrapper = styled.div`
  padding: 20px 0 0 20px;
  cursor: pointer;
  position: fixed;
  z-index: 1;
`;

const MediaModal = ({
  setModalState,
  currentTabView,
  primaryCategories,
  currentVideo,
  currentImage,
}: MediaModalProps) => {
  const currentSku = useSelector((state: State) => selectCurrentSku(state));
  const brandName = useSelector((state: State) => selectBrandName(state));
  const newMedia =
    useSelector((state: State) => selectProductMedia(state)) || [];
  const [currentTab, setCurrentTab] = useState(currentTabView);
  const videos = newMedia.filter((item) => item.mediaType === 'video');
  const relatedVideos =
    useSelector((state: State) => selectProductRelatedVideo(state)) || [];
  const user = useSelector((state: State) => state.auth?.user);
  const Videos = videos.concat(relatedVideos || []);
  const [selectedImage, setSelectedImage] = useState(currentImage);
  const [selectedVideo, setSelectedVideo] = useState(currentVideo);
  const [showFullScreen, setFullScreen] = useState(false);
  const { earlyAccessData } = currentSku || {};
  const { disablePriveProduct } = earlyAccessData || {};
  const disableCta = useShouldDisableCta();
  const onTabSwitch = (tab: string) => {
    setCurrentTab(tab);
    const tabType = tab === VIDEO_TYPE ? 'Video' : 'Image';
    const slidePos = newMedia.findIndex(
      (item) => item?.id === selectedVideo?.id
    );
    const event =
      tab === VIDEO_TYPE ? PDP_VIDEO_POPUP_OPEN : PDP_IMAGE_POPUP_OPEN;
    pushEvent(event, {
      pdpVideoData: {
        productId: currentSku?.childId,
        videoId: selectedVideo?.id,
        pdpvideoPosition: `popup:${tabType}:${slidePos + 1}`,
      },
    });
  };

  const isVideoTab = currentTab === 'video';
  const { onlyWishlistButton, inStock } = currentSku;

  // for tab safari width issue
  const onRefChange = useCallback((node) => {
    const width = window?.innerWidth;
    if (node && width >= 768 && width <= 1024 && node?.parentElement) {
      node.parentElement.style.width = '750px';
      node.parentElement.style.height = '60%';
    }
  }, []);

  return (
    <Modal
      onHide={() => setModalState(false)}
      modalBackground="transparent"
      contentStyle={modalStyle}
      allowBackdropClick
      modalContentClass="media-modal-wrapper"
    >
      <InnerWrap
        isVideo={isVideoTab}
        ref={onRefChange}
      >
        <ContentWrapper>
          <CloseIconWrapper
            onClick={() => {
              setModalState(false);
            }}
          >
            <CrossIconBlack />
          </CloseIconWrapper>
          <SelectedMedia
            isVideoTab={isVideoTab}
            Videos={Videos}
            selectedVideo={selectedVideo}
            newMedia={newMedia}
            showFullScreen={showFullScreen}
            setFullScreen={setFullScreen}
            selectedImage={selectedImage}
          />
          {!showFullScreen && (
            <ContentSection isVideo={isVideoTab}>
              <MediaSelector
                isVideo={isVideoTab}
                onTabSwitch={onTabSwitch}
                currentTabView={currentTab}
              />
              <MediaSection
                selectedVideo={selectedVideo}
                selectedImage={selectedImage}
                setSelectedImage={setSelectedImage}
                setSelectedVideo={setSelectedVideo}
                isVideoTab={isVideoTab}
              />
              <ActionWrappers
                isVideo={isVideoTab}
                isDisabled={disablePriveProduct && disableCta}
              >
                {inStock || onlyWishlistButton ? (
                  <>
                    <TileView
                      productId={currentSku?.childId}
                      productIcon={currentSku?.imageUrl}
                      productTitle={currentSku?.title}
                      mrp={currentSku?.mrp}
                      price={currentSku?.offerPrice}
                      discount={currentSku?.discount}
                      customClass="video-tile"
                    />
                    {onlyWishlistButton ? (
                      <WishlistIcon
                        pageLocation={
                          isVideoTab
                            ? PageSource.VIDEO_POPUP
                            : PageSource.IMAGE_POPUP
                        }
                        customWishlistClass={CUSTOM_WISHLIST_CLASS_VIDEO_MODAL}
                        videoId={selectedVideo?.id}
                      />
                    ) : null}
                    {!onlyWishlistButton ? (
                      <AddToCartButton
                        pageLocation={
                          isVideoTab
                            ? PageSource.VIDEO_POPUP
                            : PageSource.IMAGE_POPUP
                        }
                        handleShowCAB={() => {}}
                        videoId={selectedVideo?.id}
                      />
                    ) : null}
                  </>
                ) : (
                  <ActionWrapper>
                    {({ handleNotifyMe }: ProductRenderProps) => (
                      <NotifyMe
                        productId={currentSku?.childId}
                        offerPrice={currentSku?.offerPrice}
                        brandName={brandName}
                        productName={currentSku?.variantName}
                        primaryCategories={primaryCategories}
                        user={user}
                        sendNotifyMe={handleNotifyMe}
                        isRegisteredViaMobile={isRegisteredViaMobile}
                        onClose={() => {}}
                        customClass="video-modal-section"
                        videoId={selectedVideo?.id}
                        pageLocation={
                          isVideoTab
                            ? PageSource.VIDEO_POPUP
                            : PageSource.IMAGE_POPUP
                        }
                        product={currentSku}
                      />
                    )}
                  </ActionWrapper>
                )}
              </ActionWrappers>
            </ContentSection>
          )}
        </ContentWrapper>
      </InnerWrap>
    </Modal>
  );
};
export default MediaModal;
